import { ThemeEvents } from '@theme/events';

/**
 * Volume tier cards: syncs selected tier to the product form quantity input.
 * Refreshes from fetched markup when the variant picker updates (block lives outside variant-picker).
 */
class BsfVolumePricing extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    this.addEventListener(
      'change',
      (e) => {
        const input = e.target;
        if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.name.startsWith('bsf-vp-tier-')) {
          return;
        }
        if (!input.checked) return;
        const qty = parseInt(input.dataset.tierQty || input.value, 10);
        if (!Number.isFinite(qty)) return;
        this.#applyQuantity(qty);
      },
      { signal }
    );

    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate, { signal });

    requestAnimationFrame(() => this.#syncRadioFromQuantity());
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    const host = this.closest('.bsf-volume-pricing-block');
    if (!host?.dataset.blockId || !this.isConnected) return;

    const vp =
      event.target instanceof HTMLElement ? /** @type {HTMLElement | null} */ (event.target.closest('variant-picker')) : null;
    if (!vp) return;

    const details = host.closest('.product-details, [data-testid="product-information-details"]');
    if (!details?.contains(vp)) return;

    /** @type {{ html?: Document; productId?: string } | undefined} */
    const data = event.detail?.data;
    const doc = data?.html;
    if (!(doc instanceof Document)) return;

    const blockId = host.dataset.blockId;
    const nextHost = doc.querySelector(`.bsf-volume-pricing-block[data-block-id="${blockId}"]`);

    if (nextHost) {
      host.replaceWith(nextHost);
    } else {
      host.remove();
    }
  };

  /** @param {Event} event */
  #onQuantitySelectorUpdate = (event) => {
    /** @type {{ cartLine?: number } | undefined} */
    const detail = event.detail;
    if (detail?.cartLine) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const sourceForm = target.closest('product-form-component');
    const ourForm = this.#getProductForm();
    if (!sourceForm || !ourForm || sourceForm !== ourForm) return;

    this.#syncRadioFromQuantity();
  };

  #getProductForm() {
    const id = this.dataset.productId;
    if (!id) return null;
    const escaped = CSS.escape(String(id));

    const details = this.closest('.product-details, [data-testid="product-information-details"]');
    if (details) {
      const form = details.querySelector(`product-form-component[data-product-id="${escaped}"]`);
      if (form) return form;
    }

    const section = this.closest('.shopify-section');
    if (section) {
      const form = section.querySelector(`product-form-component[data-product-id="${escaped}"]`);
      if (form) return form;
    }

    return document.querySelector(`product-form-component[data-product-id="${escaped}"]`);
  }

  #readQuantityInput() {
    const form = this.#getProductForm();
    if (!form) return 1;
    const input = form.querySelector('input[name="quantity"]');
    if (!(input instanceof HTMLInputElement)) return 1;
    const v = parseInt(input.value, 10);
    return Number.isFinite(v) && v > 0 ? v : 1;
  }

  #tierValues() {
    const radios = this.querySelectorAll('input[type="radio"][data-tier-qty]');
    const tiers = [];
    for (const r of radios) {
      const q = parseInt(r.getAttribute('data-tier-qty') || '', 10);
      if (Number.isFinite(q)) tiers.push(q);
    }
    return tiers.sort((a, b) => a - b);
  }

  /** Pick largest tier <= qty, else smallest tier */
  #tierForQuantity(qty) {
    const tiers = this.#tierValues();
    if (!tiers.length) return null;
    let best = tiers[0];
    for (const t of tiers) {
      if (t <= qty) best = t;
    }
    return best;
  }

  #syncRadioFromQuantity() {
    const qty = this.#readQuantityInput();
    const tier = this.#tierForQuantity(qty);
    if (tier == null) return;
    const radio = this.querySelector(`input[type="radio"][data-tier-qty="${tier}"]`);
    if (!(radio instanceof HTMLInputElement)) return;
    if (!radio.checked) {
      radio.checked = true;
    }
  }

  /** @param {number} qty */
  #applyQuantity(qty) {
    const form = this.#getProductForm();
    if (!form) return;
    const input = form.querySelector('input[name="quantity"]');
    if (!(input instanceof HTMLInputElement)) return;

    const min = parseInt(input.getAttribute('min') || '1', 10) || 1;
    const step = parseInt(input.getAttribute('step') || '1', 10) || 1;
    let v = Math.max(min, qty);
    const maxAttr = input.getAttribute('max');
    if (maxAttr) {
      const max = parseInt(maxAttr, 10);
      if (Number.isFinite(max)) v = Math.min(max, v);
    }
    if ((v - min) % step !== 0) {
      v = min + Math.floor((v - min) / step) * step;
    }

    input.value = String(v);

    const qs = form.querySelector('quantity-selector-component');
    if (qs && 'onQuantityChange' in qs && typeof qs.onQuantityChange === 'function') {
      qs.onQuantityChange();
    } else {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (qs && 'updateButtonStates' in qs && typeof qs.updateButtonStates === 'function') {
      qs.updateButtonStates();
    }
  }
}

if (!customElements.get('bsf-volume-pricing')) {
  customElements.define('bsf-volume-pricing', BsfVolumePricing);
}
